import {
  formatDateWithDynamicTimezone,
  formatIDR
} from "@marketplace/shared/utils";
import {
  Button,
  ConditionalRendering,
  LoadingOverlay,
  Modal,
  Text,
  TextInput
} from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import { useRouteContext } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { formatWithOffset } from "@/utils";

import { voucherApprovalModalAtom } from "../../atoms";
import { usePostApproveVoucherMutation } from "../../hooks/mutations/use-post-approve-voucher-mutation";
import { useGetVoucherDetailQuery } from "../../hooks/queries/use-get-voucher-detail-query";

const formatDate = (date?: string) => {
  if (!date) return "-";
  return formatWithOffset(date, "dd MMM yyyy");
};

export const VoucherApprovalModal = () => {
  const { t } = useTranslation("voucher", {
    keyPrefix: "modal.approvalModal"
  });
  const context = useRouteContext({
    from: "/vouchers/",
    select: (context) => context
  });
  const [voucherApprovalModal, setVoucherApprovalModal] = useAtom(
    voucherApprovalModalAtom
  );

  const { data: voucherDetailData, isLoading } = useGetVoucherDetailQuery({
    queryParams: {
      voucherId: voucherApprovalModal.data?.voucherId!
    },
    options: {
      enabled:
        !!voucherApprovalModal.data?.voucherId && voucherApprovalModal.visible
    }
  });

  const { mutateAsync: approveVoucher, isPending: isApproving } =
    usePostApproveVoucherMutation();

  const userRoles = context.auth.userData?.roles || [];
  const canApproveOrReject = userRoles.some((role) =>
    ["seller", "seller-admin", "superadmin"].includes(role)
  );

  const handleClose = () => {
    setVoucherApprovalModal({ visible: false, data: null });
  };

  const handleApprove = async () => {
    if (!voucherApprovalModal.data?.voucherId) return;

    try {
      await approveVoucher({
        voucherId: voucherApprovalModal.data.voucherId,
        status: "approved"
      });

      notifications.show({
        title: t("notifications.approveSuccess.title"),
        message: t("notifications.approveSuccess.message")
      });

      handleClose();
    } catch {
      notifications.show({
        title: t("notifications.approveError.title"),
        message: t("notifications.approveError.message"),
        variant: "error"
      });
    }
  };

  const handleReject = async () => {
    if (!voucherApprovalModal.data?.voucherId) return;

    try {
      await approveVoucher({
        voucherId: voucherApprovalModal.data.voucherId,
        status: "rejected"
      });

      notifications.show({
        title: t("notifications.rejectSuccess.title"),
        message: t("notifications.rejectSuccess.message")
      });

      handleClose();
    } catch {
      notifications.show({
        title: t("notifications.rejectError.title"),
        message: t("notifications.rejectError.message"),
        variant: "error"
      });
    }
  };

  const voucher = voucherDetailData?.data;
  const priceAfter = voucher?.price_before
    ? voucher.price_before - (voucher.price_discount || 0)
    : 0;
  const period = voucher
    ? `${formatDate(voucher.valid_start_date)} - ${formatDate(voucher.valid_end_date)}`
    : "-";
  const createdAt = voucher?.created_at
    ? formatDateWithDynamicTimezone(voucher.created_at, true)
    : "-";

  return (
    <Modal
      opened={voucherApprovalModal.visible}
      onClose={handleClose}
      title={
        <div className="flex justify-between items-center">
          <Text type="heading" weight={600} className="text-fjord-grey-500">
            {t("title")}
          </Text>
          <Text type="paragraph" size="s" className="text-fjord-grey-300">
            {t("createdAt", { date: createdAt })}
          </Text>
        </div>
      }
      size={610}
      withCloseButton={false}
      classNames={{
        title: "w-full",
        body: "!pt-0"
      }}
    >
      <LoadingOverlay visible={isLoading || isApproving} />
      <div className="space-y-4">
        {/* Customer Name */}
        <div>
          <Text type="paragraph" size="s" className="text-fjord-grey-300 mb-2">
            {t("customerName")}
          </Text>
          <TextInput
            value={voucher?.customer_name || voucher?.email_customer || "-"}
            size="lg"
            readOnly
            classNames={{
              input: "bg-gray-50"
            }}
          />
        </div>

        {/* Company Name */}
        <div>
          <Text type="paragraph" size="s" className="text-fjord-grey-300 mb-2">
            {t("companyName")}
          </Text>
          <TextInput
            value={voucher?.company_name || "-"}
            size="lg"
            readOnly
            classNames={{
              input: "bg-gray-50"
            }}
          />
        </div>

        {/* Sales Name */}
        <div>
          <Text type="paragraph" size="s" className="text-fjord-grey-300 mb-2">
            {t("salesName")}
          </Text>
          <TextInput
            value={voucher?.sales_name || "-"}
            size="lg"
            readOnly
            classNames={{
              input: "bg-gray-50"
            }}
          />
        </div>

        {/* Product Name */}
        <div>
          <Text type="paragraph" size="s" className="text-fjord-grey-300 mb-2">
            {t("productName")}
          </Text>
          <TextInput
            value={voucher?.product_name || "-"}
            size="lg"
            readOnly
            classNames={{
              input: "bg-gray-50"
            }}
          />
        </div>

        {/* Product Package */}
        <div>
          <Text type="paragraph" size="s" className="text-fjord-grey-300 mb-2">
            {t("productPackage")}
          </Text>
          <TextInput
            value={voucher?.product_package_name || "-"}
            size="lg"
            readOnly
            classNames={{
              input: "bg-gray-50"
            }}
          />
        </div>

        {/* Variant */}
        <div>
          <Text type="paragraph" size="s" className="text-fjord-grey-300 mb-2">
            {t("variant")}
          </Text>
          <TextInput
            value={voucher?.variant_name || "-"}
            size="lg"
            readOnly
            classNames={{
              input: "bg-gray-50"
            }}
          />
        </div>

        {/* Price Before and After */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Text className="text-fjord-grey-300 mb-2">{t("priceBefore")}</Text>
            <Text type="heading" size="xl" className="text-fjord-grey-500">
              {voucher?.price_before ? formatIDR(voucher.price_before) : "-"}
            </Text>
          </div>
          <div>
            <Text className="text-fjord-grey-300 mb-2">{t("priceAfter")}</Text>
            <Text type="heading" size="xl" className="text-fjord-grey-500">
              {formatIDR(priceAfter)}
            </Text>
            <Text size="s" className="text-fjord-grey-400 mt-1">
              {t("includesFees")}
            </Text>
          </div>
        </div>

        {/* Period */}
        <div>
          <Text className="text-fjord-grey-300 mb-1">{t("period")}</Text>
          <Text size="xl" className="text-fjord-grey-500">
            {period}
          </Text>
        </div>

        <hr className="bg-mischka-100 h-px border-0" />

        {/* Action Buttons */}
        <div className="flex justify-end items-end gap-x-[14px] grow">
          <ConditionalRendering
            condition={canApproveOrReject && voucher?.status === "on_review"}
          >
            <Button
              variant="danger"
              color="red"
              onClick={handleReject}
              className="w-[128px]"
            >
              {t("reject")}
            </Button>
            <Button
              variant="primary"
              onClick={handleApprove}
              className="w-[128px]"
            >
              {t("approve")}
            </Button>
          </ConditionalRendering>

          <ConditionalRendering
            condition={!canApproveOrReject || voucher?.status !== "on_review"}
          >
            <Button
              variant="secondary"
              onClick={handleClose}
              className="w-[128px]"
            >
              {t("back")}
            </Button>
          </ConditionalRendering>
        </div>
      </div>
    </Modal>
  );
};
